import React, { useRef, useEffect, useState } from "react";
import { 
  Bold, Italic, Underline, Strikethrough, 
  Heading1, Heading2, List, ListOrdered, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link, Unlink, RemoveFormatting, Code, Image as ImageIcon, Loader2,
  Copy, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/supabase";

interface HtmlEditorProps {
  value: string;
  onChange: (val: string) => void;
  className?: string;
}

export function HtmlEditor({ value, onChange, className = "" }: HtmlEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCode, setShowCode] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [selectedImg, setSelectedImg] = useState<HTMLImageElement | null>(null);
  const [imgMenuPos, setImgMenuPos] = useState<{ x: number; y: number } | null>(null);

  // Keep internal state in sync with prop, but avoid cursor jump
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || "<p><br></p>";
    }
    setHtmlValue(value || "");
  }, [value]);

  const handleEditorMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      e.preventDefault();
      const img = target as HTMLImageElement;
      setSelectedImg(img);
      
      // Add border to show selection
      img.style.border = '2px solid #3b82f6';
      
      // Calculate menu position using viewport coordinates
      const imgRect = img.getBoundingClientRect();
      
      // Position menu above the image or to the right if there's space
      let x = imgRect.left;
      let y = imgRect.top - 10; // Above the image
      
      // If menu would go off top, put it below
      if (y < 250) {
        y = imgRect.bottom + 10;
      }
      
      setImgMenuPos({
        x: x,
        y: y
      });
    } else {
      // Deselect image when clicking elsewhere
      if (selectedImg) {
        selectedImg.style.border = '';
        setSelectedImg(null);
        setImgMenuPos(null);
      }
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      onChange(html);
    }
  };

  const exec = (command: string, value: string = "") => {
    document.execCommand(command, false, value);
    handleInput();
  };

  const handleLink = () => {
    const url = prompt("Link URL'si girin:");
    if (url) {
      exec("createLink", url);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel dosyası seçin.');
      return;
    }

    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result as string;
        
        // Call the upload server function
        const result = await api.post<any>('/settings/upload.php', { fileName: file.name, base64Data });
        const imageUrl = result.data ? result.data.url : result.url;

        // Insert image into editor
        if (editorRef.current) {
          const img = document.createElement('img');
          img.src = imageUrl;
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
          img.style.marginBottom = '10px';
          img.style.cursor = 'pointer';
          img.style.display = 'block';
          
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.insertNode(img);
            range.setStartAfter(img);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            editorRef.current.appendChild(img);
          }
          
          handleInput();
          toast.success('Görsel başarıyla eklendi! Boyutlandırmak için görsele tıkla.');
        }
      } catch (err: any) {
        toast.error('Görsel yükleme hatası: ' + err.message);
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.onerror = () => {
      toast.error('Dosya okunamadı.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const updateImageStyle = (property: string, value: string) => {
    if (selectedImg) {
      selectedImg.style[property as any] = value;
      handleInput();
    }
  };

  const setImageWidth = (width: string) => {
    if (selectedImg) {
      selectedImg.style.width = width;
      selectedImg.style.height = 'auto';
      handleInput();
    }
  };

  const setImageAlignment = (align: 'left' | 'center' | 'right') => {
    if (selectedImg) {
      selectedImg.style.display = 'block';
      selectedImg.style.float = 'none';
      
      if (align === 'center') {
        selectedImg.style.margin = '10px auto';
        selectedImg.style.textAlign = 'center';
      } else if (align === 'right') {
        selectedImg.style.float = 'right';
        selectedImg.style.margin = '0 0 10px 10px';
      } else {
        selectedImg.style.float = 'left';
        selectedImg.style.margin = '0 10px 10px 0';
      }
      handleInput();
    }
  };

  const removeImage = () => {
    if (selectedImg) {
      selectedImg.remove();
      setSelectedImg(null);
      setImgMenuPos(null);
      handleInput();
      toast.success('Görsel silindi');
    }
  };

  return (
    <div className={`border rounded-xl bg-background overflow-hidden flex flex-col focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15 transition-all ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b bg-accent/30 text-muted-foreground select-none">
        <button
          type="button"
          onClick={() => exec("bold")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Kalın"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("italic")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="İtalik"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("underline")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Altı Çizili"
        >
          <Underline size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("strikeThrough")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Üstü Çizili"
        >
          <Strikethrough size={16} />
        </button>

        <div className="w-[1px] h-4 bg-border mx-1" />

        <button
          type="button"
          onClick={() => exec("formatBlock", "<h1>")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Başlık 1"
        >
          <Heading1 size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("formatBlock", "<h2>")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Başlık 2"
        >
          <Heading2 size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("formatBlock", "<p>")}
          className="p-1 text-xs font-bold hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors px-1.5"
          title="Paragraf"
        >
          P
        </button>

        <div className="w-[1px] h-4 bg-border mx-1" />

        <button
          type="button"
          onClick={() => exec("insertUnorderedList")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Sırasız Liste"
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("insertOrderedList")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Sıralı Liste"
        >
          <ListOrdered size={16} />
        </button>

        <div className="w-[1px] h-4 bg-border mx-1" />

        <button
          type="button"
          onClick={() => exec("justifyLeft")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Sola Hizala"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("justifyCenter")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Ortala"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("justifyRight")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Sağa Hizala"
        >
          <AlignRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("justifyFull")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="İki Yana Yasla"
        >
          <AlignJustify size={16} />
        </button>

        <div className="w-[1px] h-4 bg-border mx-1" />

        <button
          type="button"
          onClick={handleLink}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Bağlantı Ekle"
        >
          <Link size={16} />
        </button>
        <button
          type="button"
          onClick={() => exec("unlink")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Bağlantıyı Kaldır"
        >
          <Unlink size={16} />
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Görsel Ekle"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => exec("removeFormat")}
          className="p-1.5 hover:bg-accent hover:text-foreground rounded cursor-pointer transition-colors"
          title="Biçimlendirmeyi Temizle"
        >
          <RemoveFormatting size={16} />
        </button>

        <div className="ml-auto flex items-center">
          <button
            type="button"
            onClick={() => setShowCode(!showCode)}
            className={`p-1.5 rounded cursor-pointer transition-colors ${
              showCode ? "bg-primary/10 text-primary hover:bg-primary/20" : "hover:bg-accent hover:text-foreground"
            }`}
            title="Kod Görünümü"
          >
            <Code size={16} />
          </button>
        </div>
      </div>

      {/* Editor Content Area */}
      <div className="relative flex-1 min-h-[220px] flex">
        {showCode ? (
          <textarea
            className="w-full h-full p-4 font-mono text-xs border-0 outline-none focus:ring-0 bg-slate-900 text-slate-100 min-h-[220px]"
            value={htmlValue}
            onChange={e => {
              setHtmlValue(e.target.value);
              onChange(e.target.value);
            }}
          />
        ) : (
          <>
            <div
              ref={editorRef}
              contentEditable
              onInput={handleInput}
              onMouseDown={handleEditorMouseDown}
              className="w-full p-4 outline-none min-h-[220px] overflow-y-auto"
              style={{ minHeight: "220px" }}
            />
            
            {/* Image Context Menu */}
            {selectedImg && imgMenuPos && (
              <div 
                className="fixed bg-white border-2 border-blue-300 rounded-lg shadow-2xl p-3 z-50"
                style={{ left: `${imgMenuPos.x}px`, top: `${imgMenuPos.y}px`, minWidth: '220px' }}
                onClick={e => e.stopPropagation()}
              >
                <div className="text-xs font-bold text-blue-600 px-2 py-1 mb-2 flex justify-between items-center bg-blue-50 rounded">
                  <span>📷 Görsel Ayarları</span>
                  <button
                    onClick={() => {
                      if (selectedImg) selectedImg.style.border = '';
                      setImgMenuPos(null);
                    }}
                    className="text-blue-400 hover:text-blue-600 text-lg leading-none font-bold"
                  >
                    ×
                  </button>
                </div>
                
                {/* Width Controls */}
                <div className="mb-3 px-2">
                  <label className="text-xs font-bold text-gray-700 block mb-2">📏 Genişlik</label>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setImageWidth('100%')}
                      className="flex-1 px-2 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded text-blue-800 font-bold transition-colors"
                    >
                      100%
                    </button>
                    <button
                      onClick={() => setImageWidth('75%')}
                      className="flex-1 px-2 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded text-blue-800 font-bold transition-colors"
                    >
                      75%
                    </button>
                    <button
                      onClick={() => setImageWidth('50%')}
                      className="flex-1 px-2 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded text-blue-800 font-bold transition-colors"
                    >
                      50%
                    </button>
                    <button
                      onClick={() => setImageWidth('25%')}
                      className="flex-1 px-2 py-1.5 text-xs bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded text-blue-800 font-bold transition-colors"
                    >
                      25%
                    </button>
                  </div>
                </div>

                {/* Alignment Controls */}
                <div className="mb-3 px-2">
                  <label className="text-xs font-bold text-gray-700 block mb-2">↔️ Hizalama</label>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setImageAlignment('left')}
                      className="flex-1 px-2 py-1.5 text-xs bg-green-100 hover:bg-green-200 border border-green-300 rounded text-green-800 font-bold transition-colors"
                    >
                      Sola
                    </button>
                    <button
                      onClick={() => setImageAlignment('center')}
                      className="flex-1 px-2 py-1.5 text-xs bg-green-100 hover:bg-green-200 border border-green-300 rounded text-green-800 font-bold transition-colors"
                    >
                      Ortala
                    </button>
                    <button
                      onClick={() => setImageAlignment('right')}
                      className="flex-1 px-2 py-1.5 text-xs bg-green-100 hover:bg-green-200 border border-green-300 rounded text-green-800 font-bold transition-colors"
                    >
                      Sağa
                    </button>
                  </div>
                </div>

                {/* Delete Button */}
                <div className="px-2 pt-2 border-t border-gray-200">
                  <button
                    onClick={removeImage}
                    className="w-full px-2 py-1.5 text-xs bg-red-100 hover:bg-red-200 border border-red-300 rounded text-red-800 font-bold flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Trash2 size={14} /> Görseli Sil
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
